/**
 * Route display name utilities for optional name/setter fields.
 * When a route has no name, generates a display name from holdColor + difficulty.
 * When a route has no setterName, returns null to hide the setter section.
 */
import { tDiffLabel, tHoldLabel, routeLabelsFor, tOr } from "../i18n";
import { compareNames, routeDisplayName } from "@sendly/shared";
import { getCompetitionRoutePoints } from "../competitionUtils";

/**
 * `route_<epoch ms>` — the doc id both route-create paths mint. The bulk
 * generator appends `_<seq>` so one batch's ids stay unique; the epoch is
 * shared across the batch on purpose (see `getRouteCreatedMs`).
 *
 * Routes predating the generator's switch off Firestore auto-ids, and outdoor
 * seed routes (`route_<wall>_<name>`), do not match — they have no epoch.
 */
const ROUTE_ID_EPOCH = /^route_(\d{12,14})(?:_\d+)?$/;

/**
 * Creation instant of a route in epoch ms, or null when the doc carries no
 * signal finer than its day.
 *
 * `route.createdAt` is a "YYYY-MM-DD" day string — deliberately, because the
 * day is all the UI ever prints — so an entire day of routes ties under it and
 * the "newest" sort fell through to Firestore's default document-id order: for
 * hand-placed routes (`route_<epoch>`) that is OLDEST first, the exact inverse
 * of what the sort promises, and for bulk-generated stubs (random auto-ids) it
 * is arbitrary. The finer signal was there all along, in the doc id — the same
 * `route_<Date.now()>` convention `scripts/backfill-malformed-dates.js`
 * recomputes broken day strings from.
 *
 * Millisecond resolution, not seconds: two routes placed inside the same second
 * still separate. The ONLY deliberate tie is a bulk batch, whose N stubs share
 * one epoch because they really were created in one instant — the name
 * tie-break then lists them 1..N instead of reversing the batch to N..1 the way
 * a per-row epoch would.
 *
 * @param {Object} route - Route document (needs `id`)
 * @returns {number|null}
 */
export function getRouteCreatedMs(route) {
  const m = ROUTE_ID_EPOCH.exec(route?.id || "");
  return m ? Number(m[1]) : null;
}

/**
 * Sort a list of routes by one of the canonical sort keys used in both the
 * Map and Routes tabs. Keep the switch arm names aligned with the i18n keys
 * (`map.sortName`, `map.sortDiffAsc`, etc.) — the Segmented controls feed
 * the same value into here.
 *
 * Returns a new array (never mutates `routes`).
 *
 * @param {Array} routes
 * @param {"name"|"diffAsc"|"diffDesc"|"rating"|"ascentsDesc"|"climbsDesc"|"newest"} sortKey
 * @param {Object} ctx - { difficulty, holdColors, t, avgRatingMap, ascentCounts }
 * @returns {Array}
 */
export function sortRoutes(routes, sortKey, ctx) {
  const list = Array.isArray(routes) ? [...routes] : [];
  const { difficulty = {}, holdColors = {}, t, avgRatingMap, ascentCounts } = ctx || {};
  const dn = (r) => getRouteDisplayName(r, difficulty, holdColors, t);
  const ratingOf = (r) => {
    if (!avgRatingMap) return 0;
    if (typeof avgRatingMap.get === "function") return avgRatingMap.get(r.id) || 0;
    return avgRatingMap[r.id] || 0;
  };
  const ascentsOf = (r) => {
    if (!ascentCounts) return 0;
    if (typeof ascentCounts.get === "function") return ascentCounts.get(r.id) || 0;
    return ascentCounts[r.id] || 0;
  };
  // Every name comparison goes through `compareNames` (natural, digit-aware)
  // — a wall numbered 1..12 must not come back as 1, 10, 11, 12, 2.
  const byName = (a, b) => compareNames(dn(a), dn(b), "hu");
  switch (sortKey) {
    case "name":
      return list.sort(byName);
    case "diffAsc":
      return list.sort((a, b) => {
        const ap = a.difficulty ? (difficulty[a.difficulty]?.points ?? 0) : -1;
        const bp = b.difficulty ? (difficulty[b.difficulty]?.points ?? 0) : -1;
        return ap - bp || byName(a, b);
      });
    case "diffDesc":
      return list.sort((a, b) => {
        const ap = a.difficulty ? (difficulty[a.difficulty]?.points ?? 0) : -1;
        const bp = b.difficulty ? (difficulty[b.difficulty]?.points ?? 0) : -1;
        return bp - ap || byName(a, b);
      });
    case "rating":
      // Same name tie-break as the count sorts: most routes share a rating (or
      // have none at all), so without it the bulk of the list came back in
      // whatever order Firestore happened to hand it over.
      return list.sort((a, b) => ratingOf(b) - ratingOf(a) || byName(a, b));
    case "ascentsDesc":
      // Descending by ascent count. Ties fall back to alphabetical so the
      // bottom of the list (uncircled / zero-climb routes) stays stable.
      return list.sort((a, b) => ascentsOf(b) - ascentsOf(a) || byName(a, b));
    case "climbsDesc":
      // Descending by TOTAL climbs (first sends + repeats). Different question
      // from `ascentsDesc`: that one ranks "how many people have done it",
      // this one "how much traffic does it carry" — and after a few weeks the
      // first saturates while the second keeps moving.
      return list.sort((a, b) =>
        (ascentsOf(b) + (Number(b.repeatCount) || 0)) - (ascentsOf(a) + (Number(a.repeatCount) || 0))
        || byName(a, b));
    case "newest":
      // Day first, then the instant, then the name.
      //
      // The day leads because it is the only key EVERY route has: the epoch
      // lives in the doc id, and the ids minted before that convention (and the
      // outdoor seed ids) carry none. Leading with the instant would mean
      // inventing one for those, which can land them in the wrong day
      // entirely — while the day is also what the card prints, so this way the
      // visible order can never contradict the visible date.
      //
      // Within a day: datable routes first, newest to oldest, then the ones we
      // cannot date as ONE block in natural name order. Interleaving the two by
      // name would let a hand-placed route split a legacy bulk batch down the
      // middle of its own numbering. Same rule as the undatable day below —
      // what we know, then what we don't.
      return list.sort((a, b) => {
        const ca = a.createdAt || "";
        const cb = b.createdAt || "";
        if (ca !== cb) return ca < cb ? 1 : -1;
        const ma = getRouteCreatedMs(a);
        const mb = getRouteCreatedMs(b);
        if ((ma == null) !== (mb == null)) return ma == null ? 1 : -1;
        if (ma != null && ma !== mb) return mb - ma;
        return byName(a, b);
      });
    default:
      return list;
  }
}

/**
 * Returns the display name for a route.
 * If the route has a name, returns it as-is.
 * Otherwise generates "{holdColor label} {difficulty label}" in the viewer's
 * language — the shared `routeDisplayName` (`@sendly/shared`), which the recap
 * and Wrapped e-mails run too, fed with the label table built from `t`.
 *
 * @param {Object} route - Route document
 * @param {Object} difficulty - Resolved difficulty config object (keys → {label, color, ...})
 * @param {Object} holdColors - Resolved holdColors config object (keys → {label, color, ...})
 * @param {Function} t - i18n translation function
 * @returns {string} Display name
 */
export function getRouteDisplayName(route, difficulty, holdColors, t) {
  return routeDisplayName(route, difficulty, holdColors, routeLabelsFor(t));
}

/**
 * Name for ONE bulk-generated route: `{prefix}{n}{suffix}`.
 *
 * The sequence number is the only part the generator owns; the prefix and the
 * suffix are free text the organiser types, so a glance at the route list later
 * says which group a stub was created for (`A1`, `A2` vs `B1`, `B2`).
 *
 * Trimmed as a whole rather than per part: a prefix typed as `"A "` still
 * yields `"A 1"`, but a stray leading/trailing space never reaches the stored
 * name. With both affixes empty the result is the plain `"1"`..`"N"` the
 * generator produced before affixes existed.
 *
 * @param {string} prefix
 * @param {number} n - 1-based sequence number
 * @param {string} suffix
 * @returns {string}
 */
export function buildBulkRouteName(prefix, n, suffix) {
  return `${prefix || ""}${n}${suffix || ""}`.trim();
}

/**
 * The whole name sequence one bulk generation produces, `start`..`start+count-1`.
 *
 * ONE source of truth for the dialog's live preview and for the batch that
 * actually writes the routes — a preview that promised `A21..A25` while the
 * writer created `A1..A5` would be worse than no preview at all.
 *
 * @param {string} prefix
 * @param {number} start - first sequence number (1-based, clamped to >= 1)
 * @param {number} count - how many names
 * @param {string} suffix
 * @returns {string[]}
 */
export function buildBulkRouteNames(prefix, start, count, suffix) {
  const n = Math.max(0, Math.floor(Number(count)) || 0);
  const from = Math.max(1, Math.floor(Number(start)) || 1);
  return Array.from({ length: n }, (_, i) => buildBulkRouteName(prefix, from + i, suffix));
}

/**
 * Label for the route an audit-log entry is about.
 *
 * The entry carries its OWN snapshot of the route (`routeName`,
 * `routeDifficulty`, `routeHoldColor`, written by the auditRouteWrite trigger)
 * rather than pointing at the live doc: the route may since have been renamed,
 * archived or deleted, and an audit entry has to stay readable on its own.
 *
 * Falls back the way the whole app does — name, else "{hold color} {grade}".
 * Entries written before the trigger stored those fields have nothing left to
 * describe the route with; the caller can hand in a `fallbackRoute` (the live
 * doc, if it still exists) to fill the gap, and failing that we say "unnamed"
 * rather than printing a raw Firestore id at a gym admin.
 *
 * @param {Object} entry - audit log entry
 * @param {Object} ctx - { difficulty, holdColors, t, fallbackRoute? }
 * @returns {string}
 */
export function getAuditRouteLabel(entry, { difficulty, holdColors, t, fallbackRoute } = {}) {
  const name = (entry?.routeName || "").trim();
  if (name) return name;
  const hasSnapshot = entry?.routeDifficulty != null || entry?.routeHoldColor != null;
  const route = hasSnapshot
    ? { difficulty: entry.routeDifficulty, holdColor: entry.routeHoldColor }
    // The live doc describes the route as it is NOW, not as it was at the time
    // of the entry — an approximation we only accept because the alternative
    // for these legacy rows is no description at all.
    : fallbackRoute || null;
  if (!route) return t("audit.entry.routeUnnamed");
  return getRouteDisplayName(route, difficulty || {}, holdColors || {}, t);
}

/**
 * One side of a `route.update` audit diff, rendered the way the app words that
 * field — not the way Firestore stores it. A gym admin reading the log knows
 * "Sárga" and "Archivált"; `holdColor: yellow → red` and `active: true → false`
 * are storage details that happen to be visible.
 *
 * `active` is handled before the empty check on purpose: a legacy route doc
 * carries no `active` field at all and the app reads that as active
 * (`active !== false`), so "—" would claim the wrong thing.
 *
 * @param {string} field - route field name (name, difficulty, holdColor, points, active, discipline)
 * @param {*} value
 * @param {Object} ctx - { difficulty, holdColors, t }
 * @returns {string}
 */
export function formatAuditRouteValue(field, value, { difficulty, holdColors, t } = {}) {
  if (field === "active") return t(value === false ? "audit.value.archived" : "audit.value.active");
  if (value === null || value === undefined || value === "") return "—";
  if (field === "difficulty") return tDiffLabel(t, value, difficulty?.[value]?.label || value);
  if (field === "holdColor") return tHoldLabel(t, value, holdColors?.[value]?.label || value);
  if (field === "discipline") return tOr(t, `discipline.${value}`, String(value));
  return String(value);
}

/**
 * Returns the setter display name for a route.
 * Returns null when setterName is empty — caller should hide the setter section.
 *
 * @param {Object} route - Route document
 * @returns {string|null} Setter name or null
 */
export function getSetterDisplayName(route) {
  if (route.setterName && route.setterName.trim() !== "") {
    return route.setterName;
  }
  return null;
}

/**
 * Resolve a UID to a display name from the scoped `users` array, falling back
 * to the full `members` collection's denormalized `displayName`, and finally
 * to an optional `unknownLabel`.
 *
 * Resolution order:
 *   1. `users[].name` — scoped user list (richer profile, but only contains
 *      active climbers/raters + friends + self, so a brand-new staff member
 *      who hasn't logged any ascents yet is NOT here)
 *   2. `members[].displayName` — every gym member has a member doc with
 *      `displayName` synced on each login (and via the syncMemberDisplayName
 *      Cloud Function), so this catches inactive setters
 *   3. `unknownLabel` (or null) when the user has truly left the gym
 *
 * Used for staff-only route attribution (creator + last editor) and could be
 * lifted into a shared helper for any other UID→name lookup.
 *
 * @param {string|null|undefined} uid
 * @param {Array<{uid:string,name?:string}>} users - Scoped users list
 * @param {Array<{uid:string,displayName?:string}>} members - Full gym members list
 * @param {string} [unknownLabel] - Optional fallback when user is missing
 * @returns {string|null}
 */
export function resolveUserName(uid, users, members, unknownLabel) {
  if (!uid) return null;

  if (Array.isArray(users)) {
    const u = users.find(x => x.uid === uid);
    const name = u?.name && u.name.trim() ? u.name.trim() : null;
    if (name) return name;
  }

  if (Array.isArray(members)) {
    const m = members.find(x => x.uid === uid);
    const name = m?.displayName && m.displayName.trim() ? m.displayName.trim() : null;
    if (name) return name;
  }

  return unknownLabel || null;
}

/**
 * Returns the display name of the gym member who originally created the route
 * (route.setter UID → name). Thin wrapper over `resolveUserName`.
 *
 * Distinct from the free-text `setterName` label that route authors fill in,
 * and distinct from `getRouteLastEditorName` which tracks recent modifications.
 *
 * @param {Object} route
 * @param {Array} users
 * @param {Array} members
 * @param {string} [unknownLabel]
 * @returns {string|null}
 */
export function getRouteCreatorName(route, users, members, unknownLabel) {
  return resolveUserName(route?.setter, users, members, unknownLabel);
}

/**
 * Returns the display name of the gym member who LAST modified the route
 * (route.lastModifiedBy UID → name), but ONLY when it differs from the
 * original creator — otherwise returns null so callers can hide the row.
 *
 * Why "only when different": every route create-write also sets
 * `lastModifiedBy = setter`, and every later edit (rename, regrade, archive,
 * reposition) bumps it. Showing the same name twice ("Hozzáadta: Anna ·
 * Módosította: Anna") is noise. Showing it only on actual divergence
 * surfaces the "someone else has touched this route" signal cleanly.
 *
 * @param {Object} route
 * @param {Array} users
 * @param {Array} members
 * @param {string} [unknownLabel]
 * @returns {string|null}
 */
export function getRouteLastEditorName(route, users, members, unknownLabel) {
  const editorUid = route?.lastModifiedBy;
  if (!editorUid) return null;
  if (editorUid === route?.setter) return null;
  return resolveUserName(editorUid, users, members, unknownLabel);
}

/**
 * Sub-line under a route's name on a COMPETITION route card — difficulty label
 * and, only where it means something, the route's competition point value.
 *
 * Both halves are optional and the separator only appears between two present
 * halves, so an ungraded route in a tops-and-zones competition yields "" and
 * the caller can drop the line entirely rather than render "? · 0p".
 *
 * The point half comes from getCompetitionRoutePoints() — never from the gym's
 * difficulty table. A gym's per-grade points say what the route pays into a
 * climber's lifetime score, which has nothing to do with how a competition
 * ranks them; printing it on a competition card read as "this is what this
 * route is worth here", and for every system except Classic Redpoint and
 * Point distribution that was simply false.
 *
 * @param {Object} route - Route document
 * @param {Object} ctx - { difficulty, settings, t, groupId, attempts, registrations }
 * @returns {string} e.g. "Kék · 40p", "Kék", "40p", or ""
 */
export function getCompetitionRouteMeta(route, ctx) {
  const { difficulty = {}, settings, t, groupId, attempts, registrations } = ctx || {};
  const diffEntry = route?.difficulty != null ? (difficulty[route.difficulty] || null) : null;
  const diffLabel = diffEntry ? tDiffLabel(t, route.difficulty, diffEntry.label) : null;
  const points = getCompetitionRoutePoints(settings, route, { groupId, attempts, registrations });
  return [diffLabel, points != null ? `${points}p` : null].filter(Boolean).join(" · ");
}
